import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FiCheckCircle, FiEdit2, FiUsers } from "react-icons/fi";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import { PersonaDetail } from "../../pages/1. Gestión de Comunidades/PersonasPage";
import PersonaIniciativa from "./PersonaIniciativa";

const SeccionParticipantes = ({ iniciativa, onIniciativaUpdated }) => {
  const participaciones = useMemo(
    () => (iniciativa.participaciones || []).filter((item) => item.inscrita),
    [iniciativa.participaciones],
  );
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPersonDetail, setShowPersonDetail] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personLoading, setPersonLoading] = useState(false);
  const [personError, setPersonError] = useState("");
  const profileBadge = (persona) => {
    if (persona.tipo === "maestra") {
      return <Badge className="initiative-participant-profile initiative-participant-profile--maestra">Maestra</Badge>;
    }
    if (persona.es_participante) {
      return <Badge className="initiative-participant-profile initiative-participant-profile--participante">Interesada participante</Badge>;
    }
    return <Badge className="initiative-participant-profile initiative-participant-profile--interesada">Interesada</Badge>;
  };

  const personBadges = (persona) => (
    <>
      {profileBadge(persona)}
      <Badge bg={persona.estado === "problema" ? "danger" : "success"}>
        {persona.estado === "problema" ? "Problema" : "OK"}
      </Badge>
      {persona.en_whatsapp && <Badge bg="success">WhatsApp</Badge>}
      {persona.certificacion_sec && <Badge bg="primary">SEC</Badge>}
    </>
  );

  useEffect(() => {
    setDraft(participaciones.map((item) => ({
      persona_id: item.persona.id,
      inscrita: item.inscrita,
      aporte_realizado: item.aporte_realizado,
      aprobada: item.aprobada,
    })));
  }, [participaciones]);

  const updateStatus = (personaId, field, checked) => {
    setDraft((current) => current.map((item) => (
      item.persona_id === personaId ? { ...item, [field]: checked } : item
    )));
  };

  const openPersonDetail = async (persona) => {
    setSelectedPerson(null);
    setPersonError("");
    setPersonLoading(true);
    setShowPersonDetail(true);
    try {
      const response = await fetch(getApiUrl(`api/personas/${persona.id}/`), {
        credentials: "include",
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || "No se pudo cargar la ficha de la persona.");
      setSelectedPerson(body);
    } catch (requestError) {
      setPersonError(requestError.message);
    } finally {
      setPersonLoading(false);
    }
  };

  const updateSelectedPerson = (person) => {
    setSelectedPerson(person);
    onIniciativaUpdated?.({
      ...iniciativa,
      participaciones: (iniciativa.participaciones || []).map((participation) => (
        participation.persona.id === person.id
          ? {
            ...participation,
            persona: {
              ...participation.persona,
              ...person,
              en_whatsapp: Boolean((person.grupos_whatsapp || []).length),
            },
          }
          : participation
      )),
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`api/iniciativas/${iniciativa.id}/participaciones/`),
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
          body: JSON.stringify({ participaciones: draft }),
        },
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body.detail || "No se pudieron actualizar los estados.");
      onIniciativaUpdated(body);
      setShow(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="actividad-info initiative-participants-section">
      <div className="initiative-section-heading initiative-tab-heading">
        <div><span>INSCRIPCIONES</span><h5>Participantes</h5><p>{participaciones.length} personas inscritas</p></div>
        <Button className="initiative-tab-primary" onClick={() => { setError(""); setShow(true); }} disabled={!participaciones.length}>
          <FiEdit2 /> Gestionar estados
        </Button>
      </div>
      {participaciones.length === 0 ? (
        <div className="initiative-tab-empty"><FiUsers /><strong>Sin participantes</strong><span>No hay personas inscritas en esta iniciativa.</span></div>
      ) : (
        <div className="initiative-tab-table-wrap">
        <Table responsive hover className="initiative-tab-table initiative-participants-table align-middle">
          <thead><tr><th>Persona</th><th>Descriptores</th><th>Inscripción</th></tr></thead>
          <tbody>{participaciones.map((item) => (
            <tr className={item.persona.estado === "problema" ? "initiative-participant-row--problem" : ""} key={item.persona.id}>
              <td data-label="Persona"><PersonaIniciativa persona={item.persona} detalle={item.persona.rut || "Sin RUT"} onClick={() => openPersonDetail(item.persona)} /></td>
              <td data-label="Descriptores">
                <div className="initiative-participant-descriptors">
                  {personBadges(item.persona)}
                </div>
              </td>
              <td data-label="Inscripción">
                <div className="initiative-enrollment-summary">
                  <div className="initiative-enrollment-title"><FiCheckCircle /><strong>Inscrita</strong></div>
                  <div className="initiative-enrollment-details">
                    <span>
                      <small>Aporte</small>
                      <strong className={item.aporte_realizado ? "is-complete" : "is-pending"}>
                        {item.aporte_realizado ? "Realizado" : "—"}
                      </strong>
                    </span>
                    <span>
                      <small>Aprobación</small>
                      <strong className={item.aprobada ? "is-complete" : "is-pending"}>
                        {item.aprobada ? "Aprobada" : "Pendiente"}
                      </strong>
                    </span>
                  </div>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </Table>
        </div>
      )}

      <Modal show={show} onHide={() => { if (!saving) setShow(false); }} centered scrollable size="lg" className="initiative-attendance-modal initiative-status-modal">
        <Modal.Header closeButton={!saving}><div><span>PARTICIPANTES</span><Modal.Title>Estados de participación</Modal.Title></div></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <div className="initiative-tab-table-wrap">
          <Table responsive className="initiative-tab-table align-middle mb-0">
            <thead><tr><th>Persona</th><th>Aporte realizado</th><th>Aprobada</th></tr></thead>
            <tbody>{participaciones.map((item) => {
              const state = draft.find((candidate) => candidate.persona_id === item.persona.id) || item;
              return <tr key={item.persona.id}>
                <td><PersonaIniciativa persona={item.persona} detalle={item.persona.rut || "Sin RUT"} onClick={() => openPersonDetail(item.persona)} /></td>
                <td><Form.Check type="switch" checked={Boolean(state.aporte_realizado)} onChange={(event) => updateStatus(item.persona.id, "aporte_realizado", event.target.checked)} aria-label={`Aporte de ${item.persona.nombre}`} /></td>
                <td><Form.Check type="switch" checked={Boolean(state.aprobada)} onChange={(event) => updateStatus(item.persona.id, "aprobada", event.target.checked)} aria-label={`Aprobación de ${item.persona.nombre}`} /></td>
              </tr>;
            })}</tbody>
          </Table>
          </div>
        </Modal.Body>
        <Modal.Footer><Button className="initiative-modal-cancel" onClick={() => setShow(false)} disabled={saving}>Cancelar</Button><Button className="initiative-modal-save" onClick={save} disabled={saving}>{saving && <Spinner animation="border" size="sm" />}Guardar estados</Button></Modal.Footer>
      </Modal>

      <Modal show={showPersonDetail} onHide={() => setShowPersonDetail(false)} size="xl" centered dialogClassName="person-detail-modal participant-person-detail-modal">
        <Modal.Header closeButton className="person-modal-topbar" aria-label="Cerrar ficha de persona" />
        <Modal.Body>
          {personLoading ? (
            <div className="people-loading"><Spinner animation="border" /><span>Cargando ficha...</span></div>
          ) : personError ? (
            <Alert variant="danger" className="mb-0">{personError}</Alert>
          ) : selectedPerson && (
            <PersonaDetail person={selectedPerson} onPersonUpdate={updateSelectedPerson} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SeccionParticipantes;
